import defaultsDeep from 'lodash/defaultsDeep';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { createErrorAnalytics } from '@atlassian/jira-polaris-lib-errors/src/controllers/index.tsx';
import { maybeWaitForPremiumModal } from '../utils.tsx';
import type { Props, State } from '../../types.tsx';
import { IMPORT_BULK_CREATE_ONBOARDING } from '../../constants.tsx';
import { normalizeError } from '../../../common/utils.tsx';

type RemoteState = {
	spotlightDismissed: boolean;
};

export const loadImportState =
	(): Action<State, Props, Promise<void>> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (getState().import.spotlightDismissed !== undefined) {
			return;
		}

		try {
			const remoteState = await getUserProperty<RemoteState>(
				accountId,
				IMPORT_BULK_CREATE_ONBOARDING,
			);

			const state = getState();

			setState({
				...state,
				import: {
					...state.import,
					...defaultsDeep(remoteState, {
						spotlightDismissed: false,
						successNotificationViewed: false,
					}),
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				const state = getState();

				setState({
					...state,
					import: {
						...state.import,
						spotlightDismissed: false,
						successNotificationViewed: false,
					},
				});

				return;
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.onboarding.import-spotlight',
				`Failed to get user property with key: ${IMPORT_BULK_CREATE_ONBOARDING}`,
				error instanceof Error ? error : new Error(String(error)),
			);
		}
	};

export const setImportSpotlightDismissed =
	(spotlightDismissed: boolean): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();
		const newImportState = {
			...state.import,
			spotlightDismissed,
		};
		setState({
			...state,
			import: newImportState,
		});

		try {
			await setUserProperties(
				accountId,
				IMPORT_BULK_CREATE_ONBOARDING,
				JSON.stringify(newImportState),
			);
		} catch (error) {
			fireErrorAnalytics(
				createErrorAnalytics('polaris.onboarding.imports-spotlight', normalizeError(error)),
			);
		}
	};

export const setSuccessNotificationViewed =
	(successNotificationViewed: boolean): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		const state = getState();
		const newImportState = {
			...state.import,
			successNotificationViewed,
		};
		setState({
			...state,
			import: newImportState,
		});

		try {
			await setUserProperties(
				accountId,
				IMPORT_BULK_CREATE_ONBOARDING,
				JSON.stringify(newImportState),
			);
		} catch (error) {
			fireErrorAnalytics(
				createErrorAnalytics('polaris.onboarding.imports-notification', normalizeError(error)),
			);
		}
	};
